
class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.newsletter-popup-overlay');
    this.dismissButtons = this.querySelectorAll('[data-popup-dismiss]');
    this.copyButton = this.querySelector('[data-copy-coupon]');
    this.form = this.querySelector('#NewsletterPopupForm');
    this.delay = (Number.parseInt(this.dataset.delay, 10) || 5) * 1000;
    this.frequency = Number.parseInt(this.dataset.frequency, 10) || 30;
    this.mode = this.dataset.mode || 'delay';
    this.storageKey = 'newsletter-popup-last-closed';
    // Marcado quando o cadastro foi concluido: o popup nao abre mais sozinho.
    this.subscribedKey = 'newsletter-popup-subscribed';
    // Posicao de scroll da pagina de fundo no momento em que o popup abriu.
    // Usada para travar o scroll sem mover a pagina e para restaurar ao fechar.
    this.lockedScrollY = 0;
  }

  // localStorage pode lancar excecao (Safari privado, cookies bloqueados).
  // Sem este try/catch o connectedCallback quebrava e o popup nunca abria.
  storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  storageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* sem armazenamento: segue sem memoria */ }
  }

  connectedCallback() {
    if (!this.overlay) return;
    const isSuccess = !!this.querySelector('.newsletter-popup__success');

    if (isSuccess) {
      // So acontece se o formulario foi enviado sem JS (fallback) e a pagina recarregou
      // com o estado de sucesso vindo do servidor. Marca como inscrito para nao reabrir sozinho.
      this.storageSet(this.subscribedKey, String(Date.now()));
      this.storageSet(this.storageKey, String(Date.now()));
    } else if (this.forcedByUrl()) {
      // Teste manual: https://usetopbrands.com.br/#popup-top5 abre o popup ignorando a frequencia.
      this.timer = window.setTimeout(() => this.open(), 400);
    } else if (this.shouldShow()) {
      if (this.mode === 'exit_intent' && window.matchMedia('(pointer: fine)').matches) {
        this.exitHandler = (event) => {
          if (event.clientY <= 0) this.open();
        };
        document.addEventListener('mouseleave', this.exitHandler, { once: true });
      } else {
        this.timer = window.setTimeout(() => this.open(), this.delay);
      }
    }

    this.dismissButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.overlay.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.overlay.classList.contains('is-visible')) this.close();
    });
    this.copyButton?.addEventListener('click', () => this.copyCoupon());

    if (this.form) this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(this)) this.open();
      });
    }

    if (isSuccess) this.open();
  }

  forcedByUrl() {
    return window.location.hash === '#popup-top5';
  }

  shouldShow() {
    if (window.location.pathname.includes('/challenge')) return false;
    if (window.Shopify?.designMode) return false;
    if (this.storageGet(this.subscribedKey)) return false;
    const lastClosed = Number.parseInt(this.storageGet(this.storageKey), 10);
    if (!lastClosed) return true;
    return (Date.now() - lastClosed) / 86400000 >= this.frequency;
  }

  // Trava o scroll do fundo sem mover a pagina (funciona no Safari iOS, onde
  // overflow: hidden sozinho nao impede o scroll por tras de um elemento fixed).
  // Fixa o body na posicao atual e compensa a largura da scrollbar (desktop)
  // para o layout nao "pular" horizontalmente ao travar.
  lockScroll() {
    this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  // Reverte exatamente o que lockScroll fez e volta para a mesma posicao,
  // sem scrollIntoView e sem depender de hash na URL.
  unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, this.lockedScrollY);
  }

  open() {
    window.clearTimeout(this.timer);
    this.lockScroll();
    this.overlay.classList.add('is-visible');
    // preventScroll evita que o foco automatico no primeiro campo role a pagina.
    this.querySelector('input, button')?.focus({ preventScroll: true });
  }

  close() {
    window.clearTimeout(this.timer);
    this.overlay.classList.remove('is-visible');
    this.unlockScroll();
    this.storageSet(this.storageKey, String(Date.now()));
  }

  // Envia o formulario via fetch, sem deixar o navegador navegar/recarregar.
  // O Shopify aceita o endpoint padrao de "/contact" com Accept: application/json,
  // que responde 200 tanto em sucesso quanto em erro de validacao (o corpo da
  // resposta e que indica o resultado), entao qualquer request que completar
  // sem erro de rede e tratado como cadastro concluido.
  async handleSubmit(event) {
    event.preventDefault();
    const form = this.form;
    if (!form || form.dataset.submitting === 'true') return;
    form.dataset.submitting = 'true';

    const submitButton = form.querySelector('.newsletter-popup__submit');
    const originalLabel = submitButton?.textContent;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'ENVIANDO...';
    }

    const existingError = form.querySelector('.newsletter-popup__error');
    existingError?.remove();

    try {
      const formData = new FormData(form);
      const response = await fetch(form.action || window.location.pathname, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('request-failed');

      this.showSuccess();
    } catch (err) {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalLabel || 'LIBERAR CUPOM';
      }
      const emailField = form.querySelector('input[type="email"]');
      const errorMessage = document.createElement('p');
      errorMessage.className = 'newsletter-popup__error';
      errorMessage.textContent = 'Revise seu e-mail e tente novamente.';
      emailField?.insertAdjacentElement('afterend', errorMessage);
    } finally {
      form.dataset.submitting = 'false';
    }
  }

  // Troca a primeira tela (formulario) pela segunda (cupom revelado) sem
  // nenhuma navegacao: apenas substitui o HTML interno do popup, que ja
  // continua exatamente na mesma posicao de scroll travada por lockScroll.
  showSuccess() {
    const content = this.querySelector('.newsletter-popup__content');
    if (!content) return;

    content.innerHTML = `
      <div class="newsletter-popup__success" tabindex="-1">
        <p class="newsletter-popup__eyebrow">SEU CUPOM ESTÁ LIBERADO</p>
        <h2 class="tb-pop__title tb-pop__title--success">Você ganhou <span class="newsletter-popup__highlight">5% OFF</span> na primeira compra.</h2>
        <p class="newsletter-popup__lead">Use o código abaixo no checkout.</p>
        <div class="newsletter-popup__coupon">
          <strong>TOP5</strong>
          <button type="button" data-copy-coupon="TOP5">COPIAR</button>
        </div>
        <p class="newsletter-popup__copy-feedback" aria-live="polite"></p>
        <button type="button" class="newsletter-popup__decline" data-popup-dismiss>Continuar comprando</button>
      </div>
    `;

    this.copyButton = this.querySelector('[data-copy-coupon]');
    this.copyButton?.addEventListener('click', () => this.copyCoupon());
    this.querySelectorAll('[data-popup-dismiss]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    // Foco no bloco de sucesso, sem rolar a pagina de fundo.
    content.querySelector('.newsletter-popup__success')?.focus({ preventScroll: true });

    this.storageSet(this.subscribedKey, String(Date.now()));
    this.storageSet(this.storageKey, String(Date.now()));
  }

  async copyCoupon() {
    const coupon = this.copyButton.dataset.copyCoupon;
    const feedback = this.querySelector('.newsletter-popup__copy-feedback');
    try {
      await navigator.clipboard.writeText(coupon);
      this.copyButton.textContent = 'COPIADO';
      feedback.textContent = 'Cupom copiado. Use TOP5 no checkout.';
    } catch {
      feedback.textContent = `Copie o código: ${coupon}`;
    }
  }
}

if (!customElements.get('newsletter-popup')) customElements.define('newsletter-popup', NewsletterPopup);
